import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { ReactComponent as BrizTVIcon } from '../../../../../assets/images/BrizTV.svg';
import { ReactComponent as LogoutIcon } from '../../../../../assets/images/Logout.svg';
import { ReactComponent as Book } from '../../../../../assets/images/accountSettings/Book.svg';
import { ReactComponent as Documents } from '../../../../../assets/images/accountSettings/Documents.svg';
import { ThemeSwitcher } from '../../ThemeSwitcher';
import { getContacts } from '../../../../../utils';
import { isBrizTvFeatureEnabled, isProductTourEnabled, currentYear } from '../../../../../constants/configuration';
import { BasicButton } from '../../../buttons/BasicButton/BasicButton';
import { AccountSettingRouteNames, GlobalRouteNames } from '../../../../../models/Common';
import { controlItemsList } from './controlItemsList';
import { useAppSelector } from '../../../../../hooks/redux';

interface SideBarProps {
  isAuth: boolean;
  handleLogout: () => void;
  handleBrizTv: () => void;
  handleClose: () => void;
  handleRunTour: () => void;
}

export const SideBar: FC<SideBarProps> = ({ isAuth, handleLogout, handleBrizTv, handleRunTour, handleClose }) => {
  const { t } = useTranslation();
  const contacts = getContacts();
  const history = useHistory();
  const location = useLocation();
  const { settings } = useAppSelector((state) => state.appReducer);
  const { userInfoState } = useAppSelector((state) => state.userInfoReducer);
  const isRegularPaymentAvailable = !userInfoState.Permissions.Payments.RegularPayment;

  const pushToLink = (path: string) => {
    if (path.includes('payment')) {
      if (location.pathname !== path) {
        history.push(path);
      } else {
        history.replace({
          pathname: path,
          search: `?period=${currentYear}`,
        });
      }
    } else {
      history.push(`${GlobalRouteNames.AccountSettingPage}${path}`);
    }
    handleClose();
  };

  const onRunTour = () => {
    if (location.pathname !== GlobalRouteNames.HomePage) {
      history.push(GlobalRouteNames.HomePage);
    }
    handleRunTour();
    handleClose();
  };

  const onLogout = () => {
    handleLogout();
    handleClose();
  };

  return (
    <nav>
      <ul className="sidebar">
        <div className="sidebar__controls">
          <ThemeSwitcher />
          {isAuth ? (
            <>
              {isBrizTvFeatureEnabled && (
                <li className="navbar__brizTV">
                  <BasicButton handleClick={handleBrizTv} className="btn-props">
                    <BrizTVIcon />
                    <span>{t('header.brizTv')}</span>
                  </BasicButton>
                </li>
              )}
              {controlItemsList
                .filter((el) => {
                  if (t(el.label) === t('accountSettings.multiAccount')) {
                    return settings?.feature?.multiaccount;
                  }
                  return true;
                })
                .map((el) => (
                  <li
                    key={el.label + el.routePath}
                    className={`icon-btn ${
                      isRegularPaymentAvailable && t(el.label) === t('accountSettings.regularPayment')
                        ? 'icon-btn-disabled'
                        : ''
                    }`}
                  >
                    <BasicButton
                      handleClick={() => pushToLink(el.routePath)}
                      className={`btn-props ${
                        isRegularPaymentAvailable && t(el.label) === t('accountSettings.regularPayment')
                          ? 'disabled'
                          : ''
                      }`}
                    >
                      {el.icon}
                      {t(el.label) === t('accountSettings.multiAccount') ? (
                        <span>{t('multiAccount.brizAccounts')}</span>
                      ) : (
                        <span>{t(el.label)}</span>
                      )}
                    </BasicButton>
                  </li>
                ))}
              {settings?.feature?.digitalDocuments && (
                <li className="icon-btn">
                  <BasicButton
                    className="btn-props"
                    handleClick={() => pushToLink(AccountSettingRouteNames.DocumentsPage)}
                  >
                    <Documents />
                    <span>{t('accountSettings.documents')}</span>
                  </BasicButton>
                </li>
              )}
              {isProductTourEnabled && (
                <li className="icon-btn icon-btn-tour">
                  <BasicButton handleClick={onRunTour} className="btn-props">
                    <Book />
                    <span>{t('productTour.takeTraining')}</span>
                  </BasicButton>
                </li>
              )}
              <hr />
              <li className="icon-btn">
                <BasicButton handleClick={onLogout} className="btn-props">
                  <LogoutIcon />
                  <span>{t('header.logOut')}</span>
                </BasicButton>
              </li>
            </>
          ) : null}
        </div>
        <div className="sidebar__contacts">
          {contacts.map((el) => {
            return el.type !== 'email' ? (
              <li key={el.contact}>
                <a href={`tel:${el.contact.trim()}`} target="_blank" rel="noreferrer">
                  {el.contact}
                </a>
              </li>
            ) : null;
          })}
        </div>
      </ul>
    </nav>
  );
};
